import React from 'react';
import clsx from 'clsx';
import { FlaskConical, ArrowLeft } from 'lucide-react';

const LoginPresenter = ({
  formData,
  isLoading,
  error,
  needsVerification,
  verificationCode,
  rememberMe,
  forgotStep,
  forgotEmail,
  forgotCode,
  forgotNewPassword,
  onSubmit,
  onVerification,
  onResendCode,
  onInputChange,
  onForgotPassword,
  onForgotSubmitEmail,
  onForgotConfirm,
  onCancelForgot,
  onForgotEmailChange,
  onForgotCodeChange,
  onForgotNewPasswordChange,
  onVerificationCodeChange,
  onRememberMeChange,
  onBackToLogin,
  onGoToSignUp,
}) => {
  if (forgotStep === 'email') {
    return (
      <ForgotPasswordEmailForm
        email={forgotEmail}
        onEmailChange={onForgotEmailChange}
        onSubmit={onForgotSubmitEmail}
        onCancel={onCancelForgot}
        isLoading={isLoading}
        error={error}
      />
    );
  }

  if (forgotStep === 'reset') {
    return (
      <ForgotPasswordResetForm
        code={forgotCode}
        newPassword={forgotNewPassword}
        onCodeChange={onForgotCodeChange}
        onPasswordChange={onForgotNewPasswordChange}
        onSubmit={onForgotConfirm}
        onCancel={onCancelForgot}
        isLoading={isLoading}
        error={error}
      />
    );
  }

  if (needsVerification) {
    return (
      <VerificationForm
        verificationCode={verificationCode}
        error={error}
        isLoading={isLoading}
        onSubmit={onVerification}
        onVerificationCodeChange={onVerificationCodeChange}
        onResendCode={onResendCode}
        onBackToLogin={onBackToLogin}
      />
    );
  }

  return (
    <LoginForm
      formData={formData}
      isLoading={isLoading}
      error={error}
      rememberMe={rememberMe}
      onSubmit={onSubmit}
      onInputChange={onInputChange}
      onForgotPassword={onForgotPassword}
      onRememberMeChange={onRememberMeChange}
      onGoToSignUp={onGoToSignUp}
    />
  );
};

// 로그인 폼 컴포넌트
const LoginForm = ({
  formData,
  isLoading,
  error,
  rememberMe,
  onSubmit,
  onInputChange,
  onForgotPassword,
  onRememberMeChange,
  onGoToSignUp,
}) => {
  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-bg-100">
      {/* 홈 버튼 */}
      <button
        onClick={handleGoHome}
        className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2.5 rounded-xl text-text-300 hover:text-text-100 hover:bg-bg-200 transition-all"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium hidden sm:inline">Home</span>
      </button>

      {/* 로그인 카드 */}
      <div className="max-w-md w-full">
        <div
          className="bg-bg-200/60 rounded-2xl overflow-hidden"
          style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}
        >
          <div className="p-8 sm:p-10">
            {/* 로고 섹션 */}
            <div className="flex flex-col items-center mb-10">
              <div className="w-16 h-16 rounded-2xl bg-sedaily-500 flex items-center justify-center mb-4">
                <FlaskConical className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-text-100">AI Labs</h1>
              <p className="text-sm text-text-400 mt-1">Sign in to get started</p>
            </div>

            <form className="space-y-5" onSubmit={onSubmit}>
              <div className="space-y-4">
                <InputField
                  id="username"
                  label="Email"
                  type="text"
                  value={formData.username}
                  onChange={(e) => onInputChange("username", e.target.value)}
                  autoComplete="username"
                  placeholder="Enter your email address"
                />

                <InputField
                  id="password"
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => onInputChange("password", e.target.value)}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                />
              </div>

              {error && <ErrorMessage message={error} />}

              <SubmitButton isLoading={isLoading} text={isLoading ? "Signing in..." : "Sign in"} />

              {/* Forgot password */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={onForgotPassword}
                  className="text-sm text-text-400 hover:text-sedaily-400 transition-colors"
                >
                  Forgot password?
                </button>
              </div>

              {/* 구분선 */}
              <div className="relative py-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full" style={{ borderTop: '1px solid hsl(var(--border-300) / 0.1)' }} />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="px-3 bg-bg-200 text-text-400">or</span>
                </div>
              </div>

              {/* 회원가입 */}
              <div className="text-center">
                <span className="text-sm text-text-400">Don't have an account? </span>
                <button
                  type="button"
                  className="text-sm font-semibold text-sedaily-400 hover:text-sedaily-300 transition-colors"
                  onClick={onGoToSignUp}
                >
                  Sign up
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

// 인증 폼 컴포넌트
const VerificationForm = ({
  verificationCode,
  error,
  isLoading,
  onSubmit,
  onVerificationCodeChange,
  onResendCode,
  onBackToLogin,
}) => (
  <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-bg-100">
    <div className="max-w-md w-full">
      <div
        className="bg-bg-200/60 rounded-2xl overflow-hidden"
        style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}
      >
        <div className="p-8 sm:p-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-text-100">Email Verification</h2>
            <p className="mt-2 text-sm text-text-400">
              Enter the 6-digit verification code sent to your email
            </p>
          </div>

          <form className="space-y-6" onSubmit={onSubmit}>
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-text-200 mb-2">
                Verification Code
              </label>
              <input
                id="code"
                name="code"
                type="text"
                maxLength="6"
                required
                value={verificationCode}
                onChange={(e) => onVerificationCodeChange(e.target.value.replace(/[^0-9]/g, ''))}
                className="w-full px-4 py-3 bg-bg-300/50 text-text-100 text-center text-2xl tracking-widest rounded-xl outline-none focus:ring-1 focus:ring-sedaily-500/30 transition-all placeholder:text-text-400"
                style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}
                placeholder="000000"
              />
            </div>

            {error && <ErrorMessage message={error} />}

            <div className="space-y-3">
              <SubmitButton isLoading={isLoading} text={isLoading ? "Verifying..." : "Verify"} />

              <button
                type="button"
                onClick={onResendCode}
                disabled={isLoading}
                className="w-full text-sm text-text-300 hover:text-text-100 transition-colors"
              >
                Resend verification code
              </button>

              <button
                type="button"
                onClick={onBackToLogin}
                className="w-full text-sm text-text-300 hover:text-text-100 transition-colors"
              >
                Back to sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
);

// 입력 필드 컴포넌트
const InputField = ({ id, label, type, value, onChange, autoComplete, placeholder }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-text-200 mb-2">
      {label}
    </label>
    <input
      id={id}
      name={id}
      type={type}
      autoComplete={autoComplete}
      required
      value={value}
      onChange={onChange}
      className="w-full px-4 py-3 bg-bg-300/50 text-text-100 rounded-xl outline-none focus:ring-1 focus:ring-sedaily-500/30 transition-all placeholder:text-text-400"
      style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}
      placeholder={placeholder}
    />
  </div>
);

// 에러 메시지 컴포넌트
const ErrorMessage = ({ message }) => (
  <div
    className="rounded-xl p-4 flex items-start gap-3 bg-red-500/10"
    style={{ border: '1px solid hsl(0 84.2% 60.2% / 0.2)' }}
  >
    <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
    <p className="text-sm flex-1 text-red-400">{message}</p>
  </div>
);

// 제출 버튼 컴포넌트
const SubmitButton = ({ isLoading, text, onClick }) => (
  <button
    type={onClick ? 'button' : 'submit'}
    onClick={onClick}
    disabled={isLoading}
    className={clsx(
      "w-full flex justify-center items-center py-3 px-4 text-sm font-medium rounded-xl text-white bg-sedaily-500 transition-all",
      isLoading ? "opacity-70 cursor-not-allowed" : "hover:bg-sedaily-600 active:scale-[0.98]"
    )}
  >
    {isLoading && (
      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    )}
    {text}
  </button>
);

// 비밀번호 찾기 - 이메일 입력 폼
const ForgotPasswordEmailForm = ({ email, onEmailChange, onSubmit, onCancel, isLoading, error }) => (
  <div className="min-h-screen flex items-center justify-center py-12 px-4 bg-bg-100">
    <div className="max-w-md w-full">
      <div className="bg-bg-200/60 rounded-2xl overflow-hidden" style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}>
        <div className="p-8 sm:p-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-text-100">Forgot Password</h2>
            <p className="mt-2 text-sm text-text-400">Enter your email to receive a reset code</p>
          </div>
          <div className="space-y-5">
            <InputField
              id="forgot-email"
              label="Email"
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="Enter your email address"
            />
            {error && <ErrorMessage message={error} />}
            <SubmitButton isLoading={isLoading} text={isLoading ? "Sending..." : "Send Reset Code"} onClick={onSubmit} />
            <button type="button" onClick={onCancel} className="w-full text-sm text-text-400 hover:text-text-200 transition-colors">
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// 비밀번호 찾기 - 코드 + 새 비밀번호 입력 폼
const ForgotPasswordResetForm = ({ code, newPassword, onCodeChange, onPasswordChange, onSubmit, onCancel, isLoading, error }) => (
  <div className="min-h-screen flex items-center justify-center py-12 px-4 bg-bg-100">
    <div className="max-w-md w-full">
      <div className="bg-bg-200/60 rounded-2xl overflow-hidden" style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}>
        <div className="p-8 sm:p-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-text-100">Reset Password</h2>
            <p className="mt-2 text-sm text-text-400">Enter the code sent to your email and your new password</p>
          </div>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-text-200 mb-2">Verification Code</label>
              <input
                type="text"
                maxLength="6"
                value={code}
                onChange={(e) => onCodeChange(e.target.value.replace(/[^0-9]/g, ''))}
                className="w-full px-4 py-3 bg-bg-300/50 text-text-100 text-center text-2xl tracking-widest rounded-xl outline-none focus:ring-1 focus:ring-sedaily-500/30 transition-all placeholder:text-text-400"
                style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}
                placeholder="000000"
              />
            </div>
            <InputField
              id="new-password"
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => onPasswordChange(e.target.value)}
              placeholder="Enter new password"
            />
            {error && <ErrorMessage message={error} />}
            <SubmitButton isLoading={isLoading} text={isLoading ? "Resetting..." : "Reset Password"} onClick={onSubmit} />
            <button type="button" onClick={onCancel} className="w-full text-sm text-text-400 hover:text-text-200 transition-colors">
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default LoginPresenter;
